import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Headphones, Plus, Edit, Trash2, Eye, EyeOff, Play, Pause, Copy, Download, 
  ArrowLeft, Upload, Image, Video, Music2, ExternalLink, BarChart3, RefreshCw, Library, Share2
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPodcasts() {
  const location = useLocation();
  const fromParam = new URLSearchParams(location.search).get('from');
  const backTo = fromParam ? decodeURIComponent(fromParam) : '/admin';
  const [podcasts, setPodcasts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPodcast, setEditingPodcast] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    series: '',
    embed_url: '',
    is_video: false,
    members_only_download: true
  });
  const [files, setFiles] = useState({
    audio: null,
    video: null,
    cover: null
  });
  const [uploading, setUploading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState(null); // 'audio', 'video', 'cover'
  const [mediaUrls, setMediaUrls] = useState({
    audio_url: '',
    video_url: '',
    cover_url: ''
  });
  const [previewId, setPreviewId] = useState(null); // id of podcast currently playing inline

  const openMediaPicker = (target) => {
    setMediaPickerTarget(target);
    setShowMediaPicker(true);
  };

  const handleMediaSelect = (selectedItems) => {
    const list = Array.isArray(selectedItems) ? selectedItems : (selectedItems ? [selectedItems] : []);
    if (list.length === 0) return;
    const item = list[0];
    const fullUrl = item.url.startsWith('http') ? item.url : `${API_URL}${item.url}`;
    
    setMediaUrls(prev => ({
      ...prev,
      [`${mediaPickerTarget}_url`]: fullUrl
    }));
    
    toast.success(`${mediaPickerTarget} selected from library!`);
    setShowMediaPicker(false);
    setMediaPickerTarget(null);
  };

  useEffect(() => {
    fetchPodcasts();
    fetchStats();
  }, []);

  const fetchPodcasts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/podcasts/admin/all`);
      const data = await response.json();
      setPodcasts(data.podcasts || []);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/podcasts/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let response;
      if (editingPodcast) {
        // Metadata-only PUT when editing an existing podcast
        response = await fetch(`${API_URL}/api/podcasts/${editingPodcast.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: formData.title,
            description: formData.description,
            category: formData.category,
            series: formData.series || null,
            embed_url: formData.embed_url || null,
            is_video: formData.is_video,
            members_only_download: formData.members_only_download,
          }),
        });
      } else {
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('category', formData.category);
        formDataToSend.append('series', formData.series || '');
        formDataToSend.append('embed_url', formData.embed_url || '');
        formDataToSend.append('is_video', formData.is_video);
        formDataToSend.append('members_only_download', formData.members_only_download);

        if (files.audio) formDataToSend.append('audio_file', files.audio);
        if (files.video) formDataToSend.append('video_file', files.video);
        if (files.cover) formDataToSend.append('cover_image', files.cover);

        response = await fetch(`${API_URL}/api/podcasts`, {
          method: 'POST',
          body: formDataToSend
        });
      }

      if (response.ok) {
        resetForm();
        fetchPodcasts();
        fetchStats();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating podcast:', error);
      alert('Failed to create podcast');
    }
    setUploading(false);
  };

  const togglePublish = async (podcast) => {
    try {
      await fetch(`${API_URL}/api/podcasts/${podcast.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ published: !podcast.published })
      });
      fetchPodcasts();
    } catch (error) {
      console.error('Error updating podcast:', error);
    }
  };

  const deletePodcast = async (podcast) => {
    if (!window.confirm(`Delete "${podcast.title}"? This cannot be undone.`)) return;
    
    try {
      await fetch(`${API_URL}/api/podcasts/${podcast.id}`, { method: 'DELETE' });
      fetchPodcasts();
      fetchStats();
    } catch (error) {
      console.error('Error deleting podcast:', error);
    }
  };

  const startEdit = (podcast) => {
    setEditingPodcast(podcast);
    setFormData({
      title: podcast.title || '',
      description: podcast.description || '',
      category: podcast.category || '',
      series: podcast.series || '',
      embed_url: podcast.embed_url || '',
      is_video: !!podcast.is_video,
      members_only_download: !!podcast.members_only_download,
    });
    setFiles({ audio: null, video: null, cover: null });
    setMediaUrls({
      audio_url: podcast.audio_url || '',
      video_url: podcast.video_url || '',
      cover_url: podcast.cover_url || '',
    });
    setShowForm(true);
  };

  const duplicatePodcast = async (podcast) => {
    try {
      const fd = new FormData();
      fd.append('title', `${podcast.title} (copy)`);
      fd.append('description', podcast.description || '');
      fd.append('category', podcast.category || '');
      fd.append('series', podcast.series || '');
      fd.append('embed_url', podcast.embed_url || '');
      fd.append('is_video', podcast.is_video || false);
      fd.append('members_only_download', podcast.members_only_download !== false);
      const res = await fetch(`${API_URL}/api/podcasts`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Duplicate failed');
      toast.success('Episode duplicated — edit the copy to customize');
      fetchPodcasts();
    } catch (e) {
      toast.error(e.message || 'Could not duplicate');
    }
  };

  const copyShareLink = async (podcast) => {
    const url = `${window.location.origin}/podcasts#${podcast.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard');
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  const togglePreview = (podcast) => {
    setPreviewId((cur) => (cur === podcast.id ? null : podcast.id));
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPodcast(null);
    setFormData({
      title: '',
      description: '',
      category: '',
      series: '',
      embed_url: '',
      is_video: false,
      members_only_download: true
    });
    setFiles({ audio: null, video: null, cover: null });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-black p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to={backTo} className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors" data-testid="admin-podcasts-back">
              <ArrowLeft className="w-5 h-5 text-zinc-400" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <Headphones className="w-7 h-7 text-purple-400" />
                Podcast Management
              </h1>
              <p className="text-zinc-400 text-sm mt-1">Upload, manage, and track your podcast episodes</p>
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-400 rounded-lg text-white font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Episode
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <div className="text-3xl font-bold text-white">{stats.total_episodes}</div>
              <div className="text-zinc-500 text-sm">Total Episodes</div>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <div className="text-3xl font-bold text-green-400">{stats.published_episodes}</div>
              <div className="text-zinc-500 text-sm">Published</div>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <div className="text-3xl font-bold text-purple-400">{stats.total_plays}</div>
              <div className="text-zinc-500 text-sm">Total Plays</div>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <div className="text-3xl font-bold text-blue-400">{stats.total_downloads}</div>
              <div className="text-zinc-500 text-sm">Downloads</div>
            </div>
          </div>
        )}

        {/* Podcast List */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="font-semibold text-white">All Episodes</h2>
            <button onClick={fetchPodcasts} className="text-zinc-400 hover:text-white">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : podcasts.length === 0 ? (
            <div className="p-8 text-center">
              <Headphones className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500">No podcasts yet. Create your first episode!</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {podcasts.map((podcast) => (
                <div key={podcast.id} className="p-4 hover:bg-zinc-800/30">
                  <div className="flex items-center gap-4">
                  {/* Cover */}
                  {podcast.cover_url ? (
                    <img loading="lazy" decoding="async" src={podcast.cover_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      {podcast.is_video ? <Video className="w-6 h-6 text-purple-400" /> : <Music2 className="w-6 h-6 text-purple-400" />}
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-white truncate" data-testid={`podcast-title-${podcast.id}`}>{podcast.title}</h3>
                      {!podcast.published && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Draft</span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-500 truncate">{podcast.category} {podcast.series && `• ${podcast.series}`}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-zinc-600">
                      <span><Play className="w-3 h-3 inline mr-1" />{podcast.play_count || 0}</span>
                      <span><Download className="w-3 h-3 inline mr-1" />{podcast.download_count || 0}</span>
                      <span>{formatDate(podcast.created_at)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {(podcast.audio_url || podcast.video_url) && (
                      <button
                        onClick={() => togglePreview(podcast)}
                        className="p-2 bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 rounded-lg transition-colors"
                        title={previewId === podcast.id ? 'Stop preview' : 'Preview'}
                        data-testid={`podcast-preview-${podcast.id}`}
                      >
                        {previewId === podcast.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      onClick={() => startEdit(podcast)}
                      className="p-2 bg-zinc-800 text-zinc-300 hover:bg-zinc-700 rounded-lg transition-colors"
                      title="Edit"
                      data-testid={`podcast-edit-${podcast.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => duplicatePodcast(podcast)}
                      className="p-2 bg-zinc-800 text-zinc-300 hover:bg-zinc-700 rounded-lg transition-colors"
                      title="Duplicate"
                      data-testid={`podcast-duplicate-${podcast.id}`}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => copyShareLink(podcast)}
                      className="p-2 bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 rounded-lg transition-colors"
                      title="Copy share link"
                      data-testid={`podcast-share-${podcast.id}`}
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => togglePublish(podcast)}
                      className={`p-2 rounded-lg transition-colors ${
                        podcast.published 
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                      title={podcast.published ? 'Unpublish' : 'Publish'}
                      data-testid={`podcast-toggle-${podcast.id}`}
                    >
                      {podcast.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => deletePodcast(podcast)}
                      className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-lg transition-colors"
                      title="Delete"
                      data-testid={`podcast-delete-${podcast.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  </div>

                  {/* Inline media preview */}
                  {previewId === podcast.id && (podcast.audio_url || podcast.video_url) && (
                    <div className="mt-3 pt-3 border-t border-zinc-800" data-testid={`podcast-player-${podcast.id}`}>
                      {podcast.is_video && podcast.video_url ? (
                        <video src={podcast.video_url} controls autoPlay className="w-full max-h-64 rounded-lg" />
                      ) : (
                        <audio src={podcast.audio_url || podcast.video_url} controls autoPlay className="w-full" />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 max-w-2xl w-full my-8"
          >
            <h2 className="text-xl font-bold text-white mb-6">
              {editingPodcast ? 'Edit Episode' : 'Upload New Episode'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  required
                />
              </div>

              {/* Category & Series */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Category *</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    placeholder="e.g., Drumming, Culture, Interview"
                    className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Series (optional)</label>
                  <input
                    type="text"
                    value={formData.series}
                    onChange={(e) => setFormData({...formData, series: e.target.value})}
                    placeholder="e.g., Healthy Drumming"
                    className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Embed URL */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">
                  Embed URL (Spotify, Apple Podcasts, etc.)
                </label>
                <input
                  type="url"
                  value={formData.embed_url}
                  onChange={(e) => setFormData({...formData, embed_url: e.target.value})}
                  placeholder="https://open.spotify.com/episode/..."
                  className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* File Uploads - Now with Media Library */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Audio File</label>
                  <button
                    type="button"
                    onClick={() => openMediaPicker('audio')}
                    className="w-full flex flex-col items-center justify-center p-4 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-500/10 transition-colors"
                  >
                    <Library className="w-6 h-6 text-green-500 mb-1" />
                    <span className="text-xs text-zinc-400">Browse Audio Library</span>
                  </button>
                  {mediaUrls.audio_url && (
                    <p className="text-xs text-green-500 mt-1 truncate">✓ Audio selected</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Video File</label>
                  <button
                    type="button"
                    onClick={() => openMediaPicker('video')}
                    className="w-full flex flex-col items-center justify-center p-4 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-500/10 transition-colors"
                  >
                    <Library className="w-6 h-6 text-purple-500 mb-1" />
                    <span className="text-xs text-zinc-400">Browse Video Library</span>
                  </button>
                  {mediaUrls.video_url && (
                    <p className="text-xs text-purple-500 mt-1 truncate">✓ Video selected</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Cover Image</label>
                  <button
                    type="button"
                    onClick={() => openMediaPicker('cover')}
                    className="w-full flex flex-col items-center justify-center p-4 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-amber-500 hover:bg-amber-500/10 transition-colors"
                  >
                    <Library className="w-6 h-6 text-amber-500 mb-1" />
                    <span className="text-xs text-zinc-400">Browse Image Library</span>
                  </button>
                  {mediaUrls.cover_url && (
                    <p className="text-xs text-amber-500 mt-1 truncate">✓ Cover selected</p>
                  )}
                </div>
              </div>

              {/* Options */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_video}
                    onChange={(e) => setFormData({...formData, is_video: e.target.checked})}
                    className="w-4 h-4 rounded border-zinc-700 text-purple-500 focus:ring-purple-500"
                  />
                  <span className="text-sm text-zinc-300">This is a video podcast</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.members_only_download}
                    onChange={(e) => setFormData({...formData, members_only_download: e.target.checked})}
                    className="w-4 h-4 rounded border-zinc-700 text-purple-500 focus:ring-purple-500"
                  />
                  <span className="text-sm text-zinc-300">Members-only download</span>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 py-3 border border-zinc-700 text-zinc-300 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 py-3 bg-purple-500 hover:bg-purple-400 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      {editingPodcast ? 'Update' : 'Upload Episode'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Media Library Picker */}
      {showMediaPicker && (
        <MediaLibraryPicker
          isOpen={showMediaPicker}
          onClose={() => {
            setShowMediaPicker(false);
            setMediaPickerTarget(null);
          }}
          onSelect={handleMediaSelect}
          multiple={false}
          filterType={mediaPickerTarget === 'cover' ? 'image' : mediaPickerTarget === 'audio' ? 'audio' : 'video'}
        />
      )}
    </div>
  );
}
